import React, { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ModuleBase, SystemFeaturesContext, UserContext } from '@/components';
import ReportCard from '@/components/report/ReportCard';
import { EntityCardSize } from '@/components/EntityCard';
import {
  ModuleBaseModel,
  ModuleBasePropsModel,
  PersonalisationConfigData,
  ReportFormat,
  SubscriptionTag,
} from '@/types';
import AccountSectionTitle from '@/components/my-account/AccountSectionTitle';
import { saveUsersSubscribedTags, getReportMatchesForWeek } from '@/api/personalisation-config';
import { useNotification } from '@/components/general/NotificationProvider';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import XLight from '@/assets/xLight.svg';

dayjs.extend(utc);

type PersonalisationConfigurationModuleModel = ModuleBaseModel<
  {
    data: PersonalisationConfigData;
  } & ModuleBasePropsModel
>;

interface WeekTab {
  ticks: number;
  text: string;
}

const PersonalisationConfigurationModule = (moduleData: PersonalisationConfigurationModuleModel) => {
  const { props } = moduleData;
  const { userSubscriptions } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const { showNotification } = useNotification();

  const formatWeekText = (date: dayjs.Dayjs) => {
    const day = date.date();
    const suffix = [1, 21, 31].includes(day)
      ? 'ST'
      : [2, 22].includes(day)
        ? 'ND'
        : [3, 23].includes(day)
          ? 'RD'
          : 'TH';
    return `${date.format('MMMM').toUpperCase()} W/C ${day}${suffix}`;
  };

  const data = props.data || props;

  const [subscriptions, setSubscriptions] = useState<SubscriptionTag[]>(data?.subscriptions || []);
  const [status, setStatus] = useState(
    data?.status || {
      hasDoneSetup: false,
      isSubscribed: false,
      wantsEmail: false,
      weeklyAlert: false,
      dailyAlert: false,
    },
  );
  const [cards, setCards] = useState(data?.cards || []);
  const [selectedWeekTabIndex, setSelectedWeekTabIndex] = useState(3);

  const startOfThisWeek = dayjs(data?.startOfThisWeek || new Date());
  const maxWeekTicks = startOfThisWeek.valueOf();
  const minWeekTicks = startOfThisWeek.clone().subtract(12, 'weeks').valueOf();

  const [weekTabs, setWeekTabs] = useState<WeekTab[]>(() => {
    const tabs: WeekTab[] = [];
    let w = startOfThisWeek.clone();
    for (let i = 0; i < 4; i++) {
      tabs.push({
        ticks: w.valueOf(),
        text: formatWeekText(w),
      });
      w = w.subtract(7, 'days');
    }
    return tabs.reverse();
  });

  const groupedSubscriptions = useMemo(() => {
    const groups = subscriptions.reduce(
      (acc, item) => {
        const group = item.group || 'Ungrouped';
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
      },
      {} as Record<string, SubscriptionTag[]>,
    );

    const groupNames = ['Directories', 'Topics', 'Generations', 'Region'];
    const result: { name: string; subs: SubscriptionTag[] }[] = [];

    groupNames.forEach((gn) => {
      if (groups[gn]) {
        result.push({
          name: gn === 'Region' ? 'Regions' : gn,
          subs: groups[gn],
        });
        delete groups[gn];
      }
    });

    const otherSubs = Object.values(groups).flat();
    if (otherSubs.length > 0) {
      result.push({ name: 'Other', subs: otherSubs });
    }

    return result;
  }, [subscriptions]);

  const selectedSubscriptions = useMemo(() => subscriptions.filter((s) => s.subscribed), [subscriptions]);

  const currentWeek = weekTabs[selectedWeekTabIndex];
  const hasNext = selectedWeekTabIndex < 3 || weekTabs[3].ticks < maxWeekTicks;
  const hasPrevious = selectedWeekTabIndex > 0 || weekTabs[0].ticks > minWeekTicks;

  const save = useCallback(
    async (statusToSave = status) => {
      const selectedTags = subscriptions.filter((sub) => sub.subscribed);
      const updatedStatus = {
        ...statusToSave,
        hasDoneSetup: true,
        isSubscribed: true,
        wantsEmail: statusToSave.weeklyAlert || statusToSave.dailyAlert,
      };

      try {
        await saveUsersSubscribedTags({
          userId: user.userId || '',
          selectedTags,
          status: updatedStatus,
        });
        showNotification('Settings Saved', 'savesuccess');
      } catch (error) {
        showNotification('Save failed.', 'error');
        console.error(error);
      }
    },
    [subscriptions, status, user.userId, showNotification],
  );

  const toggleSubscription = (item: SubscriptionTag) => {
    setSubscriptions((prev) => prev.map((s) => (s.tagId === item.tagId ? { ...s, subscribed: !s.subscribed } : s)));
    setTimeout(save, 0);
  };

  const selectNone = () => {
    setSubscriptions((prev) => prev.map((s) => ({ ...s, subscribed: false })));
    setTimeout(save, 0);
  };

  const setEfreq = (freq: 'None' | 'Weekly' | 'Daily') => {
    const newStatus = { ...status };
    if (freq === 'None') {
      newStatus.dailyAlert = false;
      newStatus.wantsEmail = false;
      newStatus.weeklyAlert = false;
    } else if (freq === 'Daily') {
      newStatus.dailyAlert = true;
      newStatus.wantsEmail = true;
      newStatus.weeklyAlert = false;
    } else {
      newStatus.dailyAlert = false;
      newStatus.wantsEmail = true;
      newStatus.weeklyAlert = true;
    }
    setStatus(newStatus);
    setTimeout(() => save(newStatus), 0);
  };

  useEffect(() => {
    if (!currentWeek) return;
    const fetchReports = async () => {
      try {
        const result = await getReportMatchesForWeek({
          userId: user.userId || '',
          startOfWeek: new Date(currentWeek.ticks).toISOString(),
        });
        setCards(result.cards || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReports();
  }, [selectedWeekTabIndex, weekTabs, user.userId]);

  const onNext = () => {
    if (selectedWeekTabIndex < 3) {
      setSelectedWeekTabIndex(selectedWeekTabIndex + 1);
    } else if (weekTabs[3].ticks < maxWeekTicks) {
      const w = dayjs.utc(weekTabs[3].ticks).add(7, 'days');
      setWeekTabs((prev) => [...prev.slice(1), { ticks: w.valueOf(), text: formatWeekText(w) }]);
    }
  };

  const onPrev = () => {
    if (selectedWeekTabIndex > 0) {
      setSelectedWeekTabIndex(selectedWeekTabIndex - 1);
    } else if (weekTabs[0].ticks > minWeekTicks) {
      const w = dayjs.utc(weekTabs[0].ticks).subtract(7, 'days');
      setWeekTabs((prev) => [{ ticks: w.valueOf(), text: formatWeekText(w) }, ...prev.slice(0, 3)]);
    }
  };

  const onTab = (index: number) => {
    setSelectedWeekTabIndex(index);
  };

  if (!userSubscriptions || user.isIpBasedIdentity) {
    return (
      <div className="mx-auto my-3 max-w-7xl px-4">
        <div className="rounded border border-gray-300 bg-gray-100 p-8 text-center">
          <p className="text-lg">
            {user.isIpBasedIdentity
              ? 'This feature is not available for IP-based users.'
              : 'This feature is not available with your current package.'}
          </p>
        </div>
      </div>
    );
  }

  if (!data || !data.subscriptions) {
    return (
      <div className="mx-auto my-3 max-w-7xl px-4">
        <div className="rounded border border-gray-300 bg-gray-100 p-8 text-center">
          <p className="text-lg">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  return (
    <ModuleBase data={moduleData} className="!pt-1">
      <div className="xxl:px-0 mx-auto my-3 max-w-7xl px-4 lg:px-5 xl:px-14">
        <div className="mb-4">
          <div className="mb-6">
            <AccountSectionTitle
              text="Personalisation"
              description="Select your preferences below to receive daily or weekly customised emails when reports publish"
            />
          </div>

          {selectedSubscriptions.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-2 text-lg font-normal">Your selection</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSubscriptions.map((item) => (
                  <button
                    key={item.tagId}
                    onClick={() => toggleSubscription(item)}
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-black px-2 py-0.5 text-xs text-white"
                  >
                    {item.name}
                    <XLight className="h-4 w-4 fill-white stroke-white stroke-6" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-6">
            <button
              onClick={selectNone}
              className="cursor-pointer rounded-2xl border-none bg-gray-100 px-3 py-1 text-sm hover:bg-gray-300"
            >
              Select None
            </button>
          </div>

          {groupedSubscriptions.map((group) => (
            <div key={group.name} className="mb-6">
              <h3 className="mb-2 text-lg font-normal">{group.name}</h3>
              <div className="flex flex-wrap gap-2">
                {group.subs.map((item) => (
                  <button
                    key={item.tagId}
                    onClick={() => toggleSubscription(item)}
                    className={`inline-flex cursor-pointer items-center gap-2 rounded-full px-3 py-1 text-sm ${
                      item.subscribed
                        ? 'bg-black text-white'
                        : 'border border-gray-300 bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    {item.name}
                    {item.subscribed && <XLight className="h-4 w-4 fill-white stroke-white stroke-6" />}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mb-6">
            <h3 className="mb-2 text-lg font-normal">Alert frequency</h3>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => setEfreq('None')}
                className={`cursor-pointer rounded-full border-1 px-6 py-2 ${!status.wantsEmail ? 'bg-black text-white' : 'bg-white text-black'}`}
              >
                None
              </button>
              <button
                onClick={() => setEfreq('Weekly')}
                className={`cursor-pointer rounded-full border-1 px-6 py-2 ${status.weeklyAlert ? 'bg-black text-white' : 'bg-white text-black'}`}
              >
                Weekly
              </button>
              <button
                onClick={() => setEfreq('Daily')}
                className={`cursor-pointer rounded-full border-1 px-6 py-2 ${status.dailyAlert ? 'bg-black text-white' : 'bg-white text-black'}`}
              >
                Daily
              </button>
            </div>
          </div>
        </div>

        <hr className="my-4 border-gray-300" />

        <div>
          <div className="grid grid-cols-12 items-center gap-2">
            <div className="col-span-1">
              <button onClick={onPrev} disabled={!hasPrevious} className="disabled:opacity-30">
                <ArrowRightLight className="h-8 w-8 rotate-180 cursor-pointer" />
              </button>
            </div>
            <div className="col-span-10 flex justify-center gap-2 lg:gap-4 xl:gap-20">
              {weekTabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => onTab(index)}
                  className={`cursor-pointer px-2 py-2 text-xs md:px-4 md:text-sm ${selectedWeekTabIndex === index ? 'border-b-2 border-black font-medium hover:bg-gray-300' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  {tab.text}
                </button>
              ))}
            </div>
            <div className="col-span-1">
              <button onClick={onNext} disabled={!hasNext} className="disabled:opacity-30">
                <ArrowRightLight className="h-8 w-8 cursor-pointer" />
              </button>
            </div>
          </div>

          <div className="mt-6">
            {cards.length === 0 ? (
              <div className="flex h-24 items-center justify-center rounded border-[3px] border-gray-400 bg-[#d3d3d3]">
                No matches
              </div>
            ) : (
              <div className="space-y-6">
                {cards.map((report, index) => (
                  <div key={index} className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 md:col-span-4">
                      <ReportCard
                        data={report}
                        size={EntityCardSize.SMALL}
                        hideTitle={report.format === ReportFormat.Report}
                        hideTopContent={report.format === ReportFormat.Report}
                        className="md:w-58 lg:w-75"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-8">
                      <div className="mb-5 flex flex-col justify-between text-base font-medium md:flex-row">
                        <span>{`${report.format} | ${report.directory?.name}`}</span>
                        <span>{dayjs(report.publishedDate).format('DD MMMM YYYY')}</span>
                      </div>
                      <div className="mb-5 text-base font-medium">{report.title}</div>
                      <div
                        className="mb-5 text-base font-medium"
                        dangerouslySetInnerHTML={{ __html: report.description }}
                      />
                      <div className="flex flex-wrap gap-2">
                        {report.subscriptionTags?.map((st: any, idx: number) => (
                          <span
                            key={idx}
                            className="rounded-full border border-black bg-black px-3 py-1 text-sm text-white"
                          >
                            {st.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default PersonalisationConfigurationModule;
